<template>
    <b-card
        no-body
    >
        <div class="card-header">
            <div
                class="card-group-title"
            >
                <h4>
                    {{ $t('org.billing.Region') }}
                </h4>
            </div>
            <a
                data-action="collapse"
                :class="{ rotate: isCollapsed }"
                @click="triggerCollapse"
                class="mr-2"
            >
                <feather-icon
                    icon="ChevronDownIcon"
                    size="16"
                />
            </a>
        </div>
        <div
            class="card-body"
            ref="REF_DYNAMICSEARCH_REGION_MAP"
        >
            <VueDatamaps
                style="margin:0 auto;"
                bubbles
                :awsRegionsConfig="awsRegionsConfig"
                :geographyConfig="geographyConfig"
                :bubblesConfig="bubblesConfig"
                :fills="fills"
                projection="Mercator"
                @custom:popup-bubble="popupTemplate"
                @click:bubble="({ event, item, index }) => handleLabelClick(item.region)"
            >
                <template v-slot:hoverBubbleInfo>
                    <div class="hoverinfo">
                        <strong>
                            {{ popupData.fullName }}
                        </strong>
                        <br>
                        <div style="min-width:100px;">
                            {{ $_getDollar(popupData.cost) }}
                        </div>
                    </div>
                </template>
            </VueDatamaps>
            <div>
                <ul class="list-unstyled">
                    <li>
                        <feather-icon
                            class="text-warning fill-current"
                            icon="CircleIcon"
                            size="16"
                        />
                        AWS Region
                    </li>
                    <li>
                        <feather-icon
                            class="text-primary fill-current"
                            icon="CircleIcon"
                            size="16"
                        />
                        AWS Edge Location
                    </li>
                    <li>
                        <feather-icon
                            class="text-danger fill-current"
                            icon="CircleIcon"
                            size="16"
                        />
                        AWS Local Zone
                    </li>
                    <li>
                        <feather-icon
                            class="text-success fill-current"
                            icon="CircleIcon"
                            size="16"
                        />
                        AWS Wavelength Zone
                    </li>
                </ul>
            </div>
        </div>

        <b-collapse
            v-model="visible"
            :visible="visible"
            class="card-content"
        >
            <b-table-simple
                sticky-header
            >
                <b-tbody>
                    <b-tr v-for="(item, index) in finalData" :key="index">
                        <b-td style="word-break:break-all;">
                            <b-form-checkbox
                                v-model="checked"
                                switch
                                inline
                                :value="item.key"
                                @change="changeSwitch"
                            >
                                <span class="switch-icon-left">
                                    <feather-icon
                                        icon="CheckIcon"/>
                                </span>
                                <label @click="handleLabelClick(item.key)">
                                    {{ __getRegionFullNameByKey(item.key) }}
                                </label>
                            </b-form-checkbox>
                        </b-td>
                        <b-td class="text-right" style="width:30%;">
                            {{ filterDataMap[item.key] ? $_getDollar(filterDataMap[item.key]): undefined }}
                        </b-td>
                    </b-tr>
                </b-tbody>
            </b-table-simple>
        </b-collapse>
    </b-card>
</template>

<script>
import useCommonData from '@/composables/useCommonData'
import { getCurrentInstance, computed, toRefs, ref, onUpdated, onMounted, onUnmounted, watch, reactive } from '@vue/composition-api'
import { VueDatamaps } from 'vue-datamaps'
export default {
    components: {
        VueDatamaps
    },
    props: {
        field: {
            type: String
        },
        fullData: {
            type: Array,
            default: function () {
                return []
            }
        },
        filterData: {
            type: Array,
            default: function () {
                return []
            }
        }
    },
    setup (props, context) {
        const vm = getCurrentInstance().proxy
        const { region, __getRegionFullNameByKey } = useCommonData()
        const { fullData, filterData, field } = toRefs(props)
        const width = ref(0)
        const popupData = reactive({
            name: '',
            code: '',
            cost: '',
            fullName: ''
        })
        const fills = {
            defaultFill: '#9fabaf',
            region: '#ff9757'
        }
        const datamapsColorSetting = ref({
            region: '#ff9f43',
            edgeLocation: '#7367f0',
            localZone: '#ea5455',
            wavelengthZone: '#28c76f'
        })
        const geographyConfig = {
            popupOnHover: false,
            highlightOnHover: false,
            borderWidth: 0.3
        }
        const visible = ref(false)
        const isCollapsed = ref(false)
        const checked = ref(filterData.value.slice().map(item => item.key))
        const sorter = computed(() => {
            return (a, b) => {
                if (a.cost < b.cost || (typeof a.cost === 'undefined' && typeof b.cost === 'number')) {
                    return 1
                } else if (typeof b.cost === 'undefined') {
                    return -1
                }
                return 0
            }
        })
        const maxCost = computed(() => Math.max.apply(null, fullData.value.map(item => item.cost)))
        const regionMap = computed(() => {
            return region.value.reduce((accumulator, currentValue) => {
                accumulator[currentValue.key] = currentValue
                return accumulator
            }, {})
        })
        const regionData = computed(() => {
            const mapData = filterData.value.reduce((accumlator, currentValue) => {
                accumlator[currentValue.key] = currentValue.cost
                return accumlator
            }, {})
            return checked.value
                .filter(item => regionMap.value[item] && regionMap.value[item].coordinates && item !== 'Any' && item !== 'Global')
                .map(item => {
                    const { key, name, fullName, coordinates, type } = regionMap.value[item]
                    const cost = mapData[item] || 0
                    return {
                        region: key,
                        name,
                        fullName,
                        cost,
                        coordinates,
                        radius: (cost / maxCost.value * 49) + 5,
                        color: fetchColorOfRegionType(type),
                        highlightFillColor: item.length === 2 ? hexToRgb(fetchColorOfRegionType(type), 0.7) : hexToRgb(fetchColorOfRegionType(type), 0.7)
                    }
                })
        })
        const fetchColorOfRegionType = (type) => {
            if (type === 'AWS Edge Location') return datamapsColorSetting.value.edgeLocation
            if (type === 'AWS Region') return datamapsColorSetting.value.region
            if (type === 'AWS Local Zone') return datamapsColorSetting.value.localZone
            if (type === 'AWS Wavelength Zone') return datamapsColorSetting.value.wavelengthZone
        }
        const awsRegionsConfig = computed(() => {
            return {
                region: region.value
            }
        })
        const bubblesConfig = computed(() => {
            return {
                borderWidth: 1,
                borderOpacity: 1,
                borderColor: '#FFFFFF',
                popupOnHover: true,
                popupTemplate: true,
                data: regionData.value,
                fillKey: 'region',
                highlightBorderColor: '#FFFFFF',
                fillOpacity: 0.55,
                animate: true,
                highlightOnHover: true,
                highlightFillColor: '#FC8D59',
                highlightBorderWidth: 2,
                highlightBorderOpacity: 1,
                highlightFillOpacity: 0.85
            }
        })
        const filterDataMap = computed(() => {
            return filterData.value.reduce((accumlator, currentValue) => {
                accumlator[currentValue.key] = currentValue.cost
                return accumlator
            }, {})
        })
        const finalData = computed(() => {
            return fullData.value.map(item => {
                return {
                    ...item,
                    cost: filterDataMap.value[item.key]
                }
            }).sort(sorter.value)
        })

        const popupTemplate = ({ datum }) => {
            popupData.cost = datum.cost
            popupData.fullName = datum.fullName
        }
        const handleLabelClick = (value) => {
            if (checked.value.length === 1 && checked.value[0] === value) {
                checked.value = finalData.value.map(item => item.key)
            } else {
                checked.value = [value]
            }
            context.emit('filter:items', { field: field.value, value: checked.value })
        }
        const changeSwitch = (value) => {
            checked.value = value.length ? value : fullData.value.map(v => v.key)
            context.emit('filter:items', { field: field.value, value: checked.value })
        }
        const triggerCollapse = () => {
            visible.value = !visible.value
            isCollapsed.value = !isCollapsed.value
        }
        const hexToRgb = (hex, alpha) => {
            const r = parseInt(hex.slice(1, 3), 16)
            const g = parseInt(hex.slice(3, 5), 16)
            const b = parseInt(hex.slice(5, 7), 16)
            if (alpha >= 0 && alpha <= 1) {
                return `rgba(${r}, ${g}, ${b}, ${alpha})`
            } else {
                return `rgb(${r}, ${g}, ${b})`
            }
        }
        // resize
        const REF_DYNAMICSEARCH_REGION_MAP = ref(null)
        const resize = () => {
            if (REF_DYNAMICSEARCH_REGION_MAP.value) {
                width.value = REF_DYNAMICSEARCH_REGION_MAP.value.getBoundingClientRect().width
            }
        }
        onUpdated(resize)
        onMounted(() => {
            vm.$nextTick(() => {
                resize()
                window.addEventListener('resize', resize)
            })
        })
        onUnmounted(() => {
            window.removeEventListener('resize', resize)
        })
        // watch
        watch(fullData, (value, oldValue) => {
            checked.value = value.map(v => v.key)
        }, {
            deep: true
        })
        return {
            awsRegionsConfig,
            popupData,
            REF_DYNAMICSEARCH_REGION_MAP,
            width,
            fills,
            geographyConfig,
            visible,
            isCollapsed,
            checked,
            filterDataMap,
            finalData,
            bubblesConfig,
            __getRegionFullNameByKey,
            changeSwitch,
            popupTemplate,
            handleLabelClick,
            triggerCollapse
        }
    }
}
</script>

<style>

</style>
